<template>
  <div class="font-select" :id="id ? id + '-wrap' : undefined">
    <v-select
      :input-id="id"
      :options="selectOptions"
      :model-value="modelValue"
      label="label"
      :reduce="reduceOption"
      :clearable="false"
      :create-item="false"
      :searchable="false"
      :append-to-body="true"
      :calculate-position="calculatePosition"
      class="font-select-control"
      @update:model-value="onSelect"
    >
      <template #option="option">
        <span class="font-select-label" :style="previewStyle(option)">{{ option.label }}</span>
      </template>
      <template #selected-option="option">
        <span class="font-select-label" :style="previewStyle(option)">{{ option.label }}</span>
      </template>
    </v-select>
  </div>
</template>

<script>
import { autoUpdate, computePosition, flip, offset, size } from '@floating-ui/dom'

const GOOGLE_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,400;0,700;1,400;1,700&family=Lato:ital,wght@0,400;0,700;1,400;1,700&family=Lora:ital,wght@0,400;0,700;1,400;1,700&family=Merriweather:ital,wght@0,400;0,700;1,400;1,700&family=Montserrat:ital,wght@0,400;0,700;1,400;1,700&family=Open+Sans:ital,wght@0,400;0,700;1,400;1,700&family=Oswald:wght@400;700&family=Playfair+Display:ital,wght@0,400;0,700;1,400;1,700&family=Rethink+Sans:ital,wght@0,400;0,700;1,400;1,700&family=Roboto:ital,wght@0,400;0,700;1,400;1,700&family=Rubik:ital,wght@0,400;0,700;1,400;1,700&display=swap'

const GOOGLE_FONTS_LINK_ID = 'johku-theme-font-preview-google'
const CORISANDE_STYLE_ID = 'johku-theme-font-preview-corisande'

let googleFontsLoaded = false
let corisandeFontsLoaded = false

function ensureGoogleFonts () {
  if (googleFontsLoaded || typeof document === 'undefined') {
    return
  }
  googleFontsLoaded = true
  if (document.getElementById(GOOGLE_FONTS_LINK_ID)) {
    return
  }
  const link = document.createElement('link')
  link.id = GOOGLE_FONTS_LINK_ID
  link.rel = 'stylesheet'
  link.href = GOOGLE_FONTS_HREF
  document.head.appendChild(link)
}

function ensureCorisandeFonts () {
  if (corisandeFontsLoaded || typeof document === 'undefined') {
    return
  }
  corisandeFontsLoaded = true
  if (document.getElementById(CORISANDE_STYLE_ID)) {
    return
  }
  const style = document.createElement('style')
  style.id = CORISANDE_STYLE_ID
  style.textContent = `
@font-face {
  font-family: 'Corisande';
  font-weight: normal;
  font-style: normal;
  font-display: swap;
  src: url('/fonts/corisande_rg-webfont.woff') format('woff');
}
@font-face {
  font-family: 'Corisande';
  font-weight: bold;
  font-style: normal;
  font-display: swap;
  src: url('/fonts/corisande_bd-webfont.woff') format('woff');
}
@font-face {
  font-family: 'Corisande Light';
  font-weight: normal;
  font-style: normal;
  font-display: swap;
  src: url('/fonts/corisande_lt-webfont.woff') format('woff');
}
`
  document.head.appendChild(style)
}

function fontLabel (stack) {
  return String(stack || '').split(',')[0].replace(/"/g, '').trim()
}

export default {
  name: 'FontSelect',
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    fonts: {
      type: Array,
      default: () => []
    },
    id: {
      type: String,
      default: ''
    },
    allowEmpty: {
      type: Boolean,
      default: false
    },
    emptyLabel: {
      type: String,
      default: 'Default'
    },
    fallbackFont: {
      type: String,
      default: '"Rethink Sans", sans-serif'
    }
  },
  computed: {
    selectOptions () {
      const options = []
      if (this.allowEmpty) {
        options.push({ value: '', label: this.emptyLabel })
      }
      for (const stack of this.fonts) {
        options.push({ value: stack, label: fontLabel(stack) })
      }
      return options
    }
  },
  mounted () {
    ensureGoogleFonts()
    ensureCorisandeFonts()
  },
  methods: {
    reduceOption (option) {
      return option && option.value != null ? option.value : ''
    },
    onSelect (value) {
      this.$emit('update:modelValue', value == null ? '' : value)
    },
    previewStyle (option) {
      const stack = option && option.value ? option.value : this.fallbackFont
      return { fontFamily: stack || this.fallbackFont }
    },
    calculatePosition (dropdownList, component) {
      const toggle = component && component.$refs && component.$refs.toggle
      if (!toggle || !dropdownList) {
        return
      }
      dropdownList.classList.add('font-select-dropdown')
      dropdownList.style.position = 'fixed'
      dropdownList.style.zIndex = '3000'
      dropdownList.style.margin = '0'
      const rect = toggle.getBoundingClientRect()
      dropdownList.style.left = `${rect.left}px`
      dropdownList.style.top = `${rect.bottom + 2}px`
      dropdownList.style.width = `${rect.width}px`
      const update = () => {
        computePosition(toggle, dropdownList, {
          placement: 'bottom-start',
          strategy: 'fixed',
          middleware: [
            offset(2),
            flip({ fallbackPlacements: ['top-start'], padding: 8 }),
            size({
              padding: 8,
              apply ({ availableHeight, rects, elements }) {
                Object.assign(elements.floating.style, {
                  width: `${rects.reference.width}px`,
                  maxHeight: `${Math.min(320, Math.max(80, availableHeight))}px`,
                  overflowY: 'auto'
                })
              }
            })
          ]
        }).then(({ x, y, placement }) => {
          Object.assign(dropdownList.style, {
            left: `${x}px`,
            top: `${y}px`
          })
          dropdownList.classList.toggle('font-select-dropdown--up', placement.startsWith('top'))
        })
      }
      let cleanup = null
      let cancelled = false
      const frame = requestAnimationFrame(() => {
        if (cancelled || !dropdownList.isConnected) {
          return
        }
        cleanup = autoUpdate(toggle, dropdownList, update)
      })
      return () => {
        cancelled = true
        cancelAnimationFrame(frame)
        if (cleanup) {
          cleanup()
        }
      }
    }
  }
}
</script>

<style scoped>
.font-select {
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
}
.font-select :deep(.v-select) {
  width: 100%;
}
.font-select :deep(.vs__dropdown-toggle) {
  min-height: 34px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  padding: 0 4px;
}
.font-select :deep(.vs__selected) {
  margin: 2px 0 0;
  padding: 0 2px;
}
.font-select :deep(.vs__search) {
  margin: 0;
  padding: 2px 0;
  height: 28px;
}
.font-select :deep(.vs__actions) {
  padding-top: 2px;
}
.font-select-label {
  display: inline-block;
  line-height: 1.4;
  font-size: 14px;
}
</style>

<style>
/* Menu is appended to body, so it cannot use scoped descendant selectors. */
.font-select-dropdown {
  border: 1px solid #ccc;
  border-radius: 0 0 4px 4px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
}
.font-select-dropdown.font-select-dropdown--up {
  border-radius: 4px 4px 0 0;
}
.font-select-dropdown .font-select-label {
  display: inline-block;
  line-height: 1.4;
  font-size: 15px;
  padding: 2px 0;
}
</style>
